/* eslint-disable camelcase */
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import InvoicePicker from './InvoicePicker'

import styles from '@/pages/app/BillingOps/styles.module.scss'

import { InvoiceSummary } from '@/api/generated'
import Input from '@/components/Input'
import { SetInvoiceStatementDescriptorInput } from '@/pages/app/BillingOps/types'

const DESCRIPTOR_PATTERN = /^(?=.*\S)[A-Za-z0-9 ]{5,22}$/

const schema = z.object({
  invoice_id: z.string().min(1, 'Invoice ID is required').startsWith('in_', 'Invoice IDs start with "in_"'),
  statement_descriptor: z
    .string()
    .regex(DESCRIPTOR_PATTERN, 'Must be 5–22 characters, letters/digits/spaces, at least one non-space'),
})

type FormValues = z.infer<typeof schema>

interface SetDescriptorFormProps {
  disabled: boolean
  onSubmit: (input: SetInvoiceStatementDescriptorInput) => void
  onChange: () => void
  formId: string
  draftInvoice: InvoiceSummary | null | undefined
  openInvoice: InvoiceSummary | null | undefined
}

const SetInvoiceStatementDescriptorForm = ({
  disabled,
  onSubmit,
  onChange,
  formId,
  draftInvoice,
  openInvoice,
}: SetDescriptorFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { invoice_id: '', statement_descriptor: '' },
  })

  const descriptorValue = useWatch({ control, name: 'statement_descriptor' }) ?? ''
  const currentInvoiceId = useWatch({ control, name: 'invoice_id' }) ?? ''
  const willRevise = openInvoice !== null && openInvoice !== undefined && currentInvoiceId === openInvoice.id
  const submit = handleSubmit((values) => onSubmit(values))

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      className={styles.section}
    >
      <div className={styles.warningBanner}>
        Statement descriptor applies to <strong>this invoice only</strong>. Stripe has no per-customer or
        per-subscription default, so future invoices fall back to the account-level descriptor. Re-run this op on each
        invoice that needs the override.
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="set-descriptor-invoice-id">
          Invoice ID
        </label>
        <InvoicePicker
          draft={draftInvoice}
          open={openInvoice}
          selectedId={currentInvoiceId || null}
          onSelect={(invoice) => {
            setValue('invoice_id', invoice.id, { shouldValidate: true })
            onChange()
          }}
        />
        <Controller
          name="invoice_id"
          control={control}
          render={({ field }) => (
            <Input
              id="set-descriptor-invoice-id"
              type="text"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="in_1AbCdEfGhIjKlMnO"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        {errors.invoice_id && <p className={styles.errorText}>{errors.invoice_id.message}</p>}
        {willRevise && (
          <div className={styles.warningBanner}>
            This invoice is already issued. Committing will <strong>void the original and reissue</strong> a revised
            invoice with the new descriptor. The customer will see two invoices in their Stripe portal — the voided
            original and the new revision — and Stripe sends a void email on the original.
          </div>
        )}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="set-descriptor-value">
          Statement Descriptor <span className={styles.charCount}>({descriptorValue.length}/22)</span>
        </label>
        <Controller
          name="statement_descriptor"
          control={control}
          render={({ field }) => (
            <Input
              id="set-descriptor-value"
              type="text"
              value={field.value}
              onInput={(text) => {
                field.onChange(text)
                onChange()
              }}
              placeholder="WISPR FLOW"
              className={styles.input}
              isDisabled={disabled}
            />
          )}
        />
        {errors.statement_descriptor && <p className={styles.errorText}>{errors.statement_descriptor.message}</p>}
      </div>
    </form>
  )
}

export default SetInvoiceStatementDescriptorForm
